import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { XCircleFill, PrinterFill, FileEarmarkTextFill, PlusCircleFill, DashCircleFill } from 'react-bootstrap-icons';
import epaLetterHead from '../img/epa_letter_head.jpg';
import '../CSS/GradeSummary.css';

const ratingOptions = [
  'Excellent',
  'Very Good',
  'Good',
  'Satisfactory',
  'Needs Improvement'
];

const predefinedComments = [
  'Good performance, keep it up',
  'Shows consistent improvement',
  'Needs to work harder',
  'Excellent participation in class',
  'Should complete assignments on time',
  'Demonstrates strong understanding',
  'Needs more practice'
];

const GradeSummaryModal = ({ show, onHide, student, summaryData }) => {
  const [formData, setFormData] = useState({
    overallComments: '',
    gender: '',
    term: 'Term 1',
    traits: [
      { trait: 'Social spirit',              rating: 'Good'       },
      { trait: 'Attitude towards learning',  rating: 'Very Good'  },
      { trait: 'Sports',                     rating: 'Satisfactory'},
      { trait: 'Work line',                  rating: 'Very Good'  }
    ],
    subjectComments: {}
  });

  const [validated, setValidated]   = useState(false);
  const [formErrors, setFormErrors] = useState({
    overallComments: false,
    gender:          false,
    term:            false,
    traits:          [],
    subjectComments: {}
  });

  // Open comment picker per subject
  const [openCommentPicker, setOpenCommentPicker] = useState(null);

  useEffect(() => {
    if (student) {
      const initialComments = {};
      if (student.subjects) {
        student.subjects.forEach(s => {
          initialComments[s.subject] = s.comments || '';
        });
      }
      setFormData(prev => ({
        ...prev,
        overallComments: student.comments || '',
        gender:          student.gender  || '',
        term:            student.term    || 'Term 1',
        subjectComments: initialComments
      }));
    }
  }, [student]);

  /* ── Handlers ── */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors(prev => ({ ...prev, [name]: false }));
  };

  const handleTraitChange = (index, field, value) => {
    const updatedTraits = [...formData.traits];
    updatedTraits[index][field] = value;
    if (field === 'rating' && value && formErrors.traits[index]) {
      const updatedErrors = [...formErrors.traits];
      updatedErrors[index] = false;
      setFormErrors(prev => ({ ...prev, traits: updatedErrors }));
    }
    setFormData(prev => ({ ...prev, traits: updatedTraits }));
  };

  const handleSubjectCommentChange = (subject, value) => {
    setFormData(prev => ({
      ...prev,
      subjectComments: { ...prev.subjectComments, [subject]: value }
    }));
  };

  const addTraitRow = () => {
    setFormData(prev => ({ ...prev, traits: [...prev.traits, { trait: '', rating: '' }] }));
    setFormErrors(prev => ({ ...prev, traits: [...prev.traits, true] }));
  };

  const removeTraitRow = (index) => {
    if (formData.traits.length <= 1) return;
    const updatedTraits  = formData.traits.filter((_, i) => i !== index);
    const updatedErrors  = formErrors.traits.filter((_, i) => i !== index);
    setFormData(prev  => ({ ...prev,  traits: updatedTraits  }));
    setFormErrors(prev => ({ ...prev, traits: updatedErrors  }));
  };

  /* ── Validation ── */
  const validateForm = () => {
    const errors = {
      overallComments: !formData.overallComments,
      gender:          !formData.gender,
      term:            !formData.term,
      traits:          formData.traits.map(t => !t.rating),
      subjectComments: {}
    };
    if (student?.subjects) {
      student.subjects.forEach(s => {
        errors.subjectComments[s.subject] = !formData.subjectComments[s.subject];
      });
    }
    setFormErrors(errors);
    return !(
      errors.overallComments ||
      errors.gender          ||
      errors.term            ||
      errors.traits.some(Boolean) ||
      Object.values(errors.subjectComments).some(Boolean)
    );
  };

  /* ── Print ── */
  const handlePrint = () => {
    if (!validateForm()) { setValidated(true); return; }

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Academic Report Card for ${student.studentName}</title>
          <style>
            @page { size: A4; margin: 0; }
            body { font-family: Arial, sans-serif; margin: 0; padding: 5mm; color: #333; font-size: 10px; }
            .header-img { width: 100%; max-height: 30mm; object-fit: contain; margin-bottom: 2mm; }
            .report-title { font-size: 12px; font-weight: bold; text-align: center; margin-bottom: 3mm; text-decoration: underline; }
            .term-info { text-align: center; margin-bottom: 3mm; font-size: 10px; }
            .particulars { margin-bottom: 3mm; font-size: 10px; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 3mm; font-size: 9px; }
            th, td { border: 1px solid #ddd; padding: 2px 3px; text-align: left; }
            th { background-color: #f2f2f2; }
            .comments { margin-bottom: 3mm; font-size: 10px; }
            .declaration { margin-top: 5mm; font-size: 10px; }
            .signature { margin-top: 10mm; }
            .contact-info { font-size: 9px; }
            .date { text-align: right; margin-bottom: 3mm; font-size: 9px; }
            .compact-table { margin-bottom: 2mm; }
            .compact-table th, .compact-table td { padding: 1px 2px; }
          </style>
        </head>
        <body>
          <div id="summary-modal-print">
            <div style="text-align:center;">
              <img src="${epaLetterHead}" alt="Eastern Pacific Academy Letterhead" class="header-img" />
            </div>
            <div class="date">${new Date().toLocaleDateString()}</div>
            <div class="report-title">Academic Report Card</div>
            <div class="term-info">
              ${formData.term} &nbsp;&nbsp;&nbsp; Year: ${student.subjects?.length > 0 ? student.subjects[0].year : new Date().getFullYear()}
            </div>
            <div class="particulars">
              <em>Student Name:</em> ${student.studentName || 'N/A'}<br>
              <em>Class:</em> ${student.class || 'N/A'}<br>
              <em>Gender:</em> ${formData.gender || 'N/A'}
            </div>
            <table class="compact-table">
              <thead>
                <tr><th>Subject</th><th>Percentage</th><th>Grading</th><th>Comments</th></tr>
              </thead>
              <tbody>
                ${student.subjects?.length > 0
                  ? student.subjects.map(s => `
                      <tr>
                        <td>${s.subject || 'N/A'}</td>
                        <td>${s.percentage || 'N/A'}%</td>
                        <td>${s.letterGrade || 'N/A'}</td>
                        <td>${formData.subjectComments[s.subject] || 'N/A'}</td>
                      </tr>`).join('')
                  : '<tr><td colspan="4">No subjects found</td></tr>'}
              </tbody>
            </table>
            <table class="compact-table">
              <thead>
                <tr><th>Social and Character Traits</th><th>Rating</th></tr>
              </thead>
              <tbody>
                ${formData.traits.map(t => `<tr><td>${t.trait}</td><td>${t.rating}</td></tr>`).join('')}
              </tbody>
            </table>
            <div class="comments">
              <strong>Overall comments:</strong><br>${formData.overallComments}
            </div>
            <div class="declaration">
              <strong>Declaration:</strong><br>
              This information is issued without alteration and erasure and is true and accurate record of the student.
              <div class="signature">
                <div style="margin-bottom:5px;">_______________</div>
                <div>Mr Jonah Java</div>
                <div>School principal</div>
                <div class="contact-info">Contact: 70121499</div>
              </div>
            </div>
          </div>
          <script>
            window.onload = function() {
              setTimeout(function() { window.print(); window.close(); }, 200);
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  /* ── Error state ── */
  if (!student) {
    return (
      <Modal show={show} onHide={onHide} size="lg" centered className="grd-summary-modal">
        <Modal.Header closeButton className="grd-summary-modal-header">
          <Modal.Title className="grd-summary-modal-title">
            <FileEarmarkTextFill /> Error Loading Report Card
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="grd-summary-modal-body">
          <div className="grd-summary-error-alert">
            Student data could not be loaded. Please try again.
          </div>
        </Modal.Body>
        <Modal.Footer className="grd-summary-modal-footer">
          <button className="grd-summary-btn grd-summary-btn--secondary" onClick={onHide}>
            <XCircleFill /> Close
          </button>
        </Modal.Footer>
      </Modal>
    );
  }

  /* ── Main render ── */
  return (
    <Modal show={show} onHide={onHide} size="xl" centered scrollable className="grd-summary-modal">
      <Modal.Header closeButton className="grd-summary-modal-header">
        <Modal.Title className="grd-summary-modal-title">
          <FileEarmarkTextFill />
          Academic Report Card — {student.studentName}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="grd-summary-modal-body">
        <div className="grd-summary-inner">

          {/* Letterhead */}
          <div className="grd-summary-letterhead">
            <img src={epaLetterHead} alt="Eastern Pacific Academy Letterhead" className="grd-summary-letterhead-img" />
          </div>

          {/* Meta row — Term + Gender */}
          <div className="grd-summary-meta-row">

            <div className="grd-summary-field">
              <label className="grd-summary-label">Term</label>
              <select
                name="term"
                value={formData.term}
                onChange={handleInputChange}
                className={`grd-summary-select${formErrors.term ? ' grd-summary-input--error' : ''}`}
              >
                <option value="Term 1">Term 1</option>
                <option value="Term 2">Term 2</option>
                <option value="Term 3">Term 3</option>
                <option value="Term 4">Term 4</option>
              </select>
              {formErrors.term && <span className="grd-summary-error-msg">Please select a term</span>}
            </div>

            <div className="grd-summary-field">
              <label className="grd-summary-label">Gender</label>
              <select
                name="gender"
                value={formData.gender}
                onChange={handleInputChange}
                className={`grd-summary-select${formErrors.gender ? ' grd-summary-input--error' : ''}`}
              >
                <option value="">Select</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
              {formErrors.gender && <span className="grd-summary-error-msg">Please select gender</span>}
            </div>

          </div>

          {/* ── Subjects Table ── */}
          <div className="grd-summary-section">
            <h6 className="grd-summary-section-title">Subject Grades</h6>
            <div className="grd-summary-table-wrap">
              <table className="grd-summary-table">
                <thead>
                  <tr className="grd-summary-table-header">
                    <th>Subject</th>
                    <th>Percentage</th>
                    <th>Grade</th>
                    <th>Comments</th>
                  </tr>
                </thead>
                <tbody>
                  {student.subjects?.length > 0 ? (
                    student.subjects.map((subject, index) => (
                      <tr key={index} className="grd-summary-table-row">
                        <td className="grd-summary-td">{subject.subject || 'N/A'}</td>
                        <td className="grd-summary-td">{subject.percentage || 'N/A'}%</td>
                        <td className="grd-summary-td">
                          <span className="grd-summary-grade-badge">{subject.letterGrade || 'N/A'}</span>
                        </td>
                        <td className="grd-summary-td">
                          <div className="grd-summary-comment-cell">
                            <textarea
                              rows={1}
                              value={formData.subjectComments[subject.subject] || ''}
                              onChange={(e) => handleSubjectCommentChange(subject.subject, e.target.value)}
                              className={`grd-summary-textarea${formErrors.subjectComments[subject.subject] ? ' grd-summary-input--error' : ''}`}
                              placeholder="Enter comment…"
                            />
                            {/* Quick-pick button */}
                            <div className="grd-summary-picker-wrap">
                              <button
                                className="grd-summary-picker-toggle"
                                title="Quick comments"
                                onClick={() => setOpenCommentPicker(openCommentPicker === index ? null : index)}
                              >
                                <i className="bi bi-list-ul" />
                              </button>
                              {openCommentPicker === index && (
                                <div className="grd-summary-picker-menu">
                                  {predefinedComments.map((comment, i) => (
                                    <button
                                      key={i}
                                      className="grd-summary-picker-item"
                                      onClick={() => {
                                        handleSubjectCommentChange(subject.subject, comment);
                                        setOpenCommentPicker(null);
                                      }}
                                    >
                                      {comment}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                          {formErrors.subjectComments[subject.subject] && (
                            <span className="grd-summary-error-msg">Please enter a comment</span>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} className="grd-summary-td grd-summary-td--empty">No subjects found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Traits Table ── */}
          <div className="grd-summary-section">
            <div className="grd-summary-section-header">
              <h6 className="grd-summary-section-title">Social & Character Traits</h6>
              <button className="grd-summary-btn grd-summary-btn--add" onClick={addTraitRow}>
                <PlusCircleFill /> Add Trait
              </button>
            </div>
            <div className="grd-summary-table-wrap">
              <table className="grd-summary-table">
                <thead>
                  <tr className="grd-summary-table-header">
                    <th style={{ width: '45%' }}>Trait</th>
                    <th>Rating</th>
                    <th style={{ width: '80px' }}>Custom</th>
                    <th style={{ width: '44px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {formData.traits.map((trait, index) => (
                    <tr key={index} className="grd-summary-table-row">
                      <td className="grd-summary-td">
                        <input
                          type="text"
                          value={trait.trait}
                          onChange={(e) => handleTraitChange(index, 'trait', e.target.value)}
                          className="grd-summary-input"
                          placeholder="Enter trait…"
                        />
                      </td>
                      <td className="grd-summary-td">
                        <select
                          value={trait.rating}
                          onChange={(e) => handleTraitChange(index, 'rating', e.target.value)}
                          className={`grd-summary-select grd-summary-select--sm${formErrors.traits[index] ? ' grd-summary-input--error' : ''}`}
                        >
                          <option value="">Select…</option>
                          {ratingOptions.map((opt, i) => (
                            <option key={i} value={opt}>{opt}</option>
                          ))}
                        </select>
                        {formErrors.traits[index] && (
                          <span className="grd-summary-error-msg">Required</span>
                        )}
                      </td>
                      <td className="grd-summary-td">
                        <input
                          type="text"
                          value={trait.rating}
                          onChange={(e) => handleTraitChange(index, 'rating', e.target.value)}
                          className="grd-summary-input"
                          placeholder="Custom…"
                        />
                      </td>
                      <td className="grd-summary-td grd-summary-td--center">
                        <button
                          className="grd-summary-remove-btn"
                          onClick={() => removeTraitRow(index)}
                          disabled={formData.traits.length <= 1}
                          title="Remove trait"
                        >
                          <DashCircleFill />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Overall Comments ── */}
          <div className="grd-summary-section">
            <div className="grd-summary-field grd-summary-field--full">
              <label className="grd-summary-label">Overall Comments</label>
              <textarea
                rows={3}
                name="overallComments"
                value={formData.overallComments}
                onChange={handleInputChange}
                className={`grd-summary-textarea grd-summary-textarea--tall${formErrors.overallComments ? ' grd-summary-input--error' : ''}`}
                placeholder="Enter overall comments…"
              />
              {formErrors.overallComments && (
                <span className="grd-summary-error-msg">Please provide overall comments</span>
              )}
            </div>
          </div>

        </div>
      </Modal.Body>

      <Modal.Footer className="grd-summary-modal-footer">
        <button className="grd-summary-btn grd-summary-btn--secondary" onClick={onHide}>
          <XCircleFill /> Close
        </button>
        <button className="grd-summary-btn grd-summary-btn--primary" onClick={handlePrint}>
          <PrinterFill /> Print Report Card
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default GradeSummaryModal;